import { AlertCircle, AlertTriangle, RotateCw } from 'lucide-react';
import type { DashboardData } from '../app/data.ts';
import type { Transaction } from '@fd/schemas';
import { useT } from '../app/i18n.tsx';
import { hrefFor } from '../app/router.tsx';
import { Money } from '../ui/primitives.tsx';

export function DashboardLoadError({
  onRetry,
  retrying,
}: {
  onRetry(): Promise<void>;
  retrying: boolean;
}) {
  const t = useT();
  return (
    <div className="dashboard-notice col-12" role="alert">
      <AlertCircle size={20} aria-hidden="true" />
      <div className="grow">
        <strong>{t.t('dashboard.loadError')}</strong>
        <p>{t.t('dashboard.loadErrorHint')}</p>
      </div>
      <button
        className="btn btn--secondary btn--sm"
        type="button"
        disabled={retrying}
        onClick={() => void onRetry()}
      >
        <RotateCw size={14} aria-hidden="true" />
        {t.t('common.retry')}
      </button>
    </div>
  );
}

/** Show the exact entries excluded by the engine; never invent a conversion rate. */
export function DashboardCurrencyNotice({
  data,
  transactions,
}: {
  data: {
    household: Pick<DashboardData['household'], 'baseCurrency'>;
    current: Pick<DashboardData['current'], 'missingBaseAmount'>;
    previous: Pick<DashboardData['previous'], 'missingBaseAmount'>;
  };
  transactions: readonly Transaction[];
}) {
  const t = useT();
  const missing = new Set([...data.current.missingBaseAmount, ...data.previous.missingBaseAmount]);
  if (missing.size === 0) return null;
  return (
    <div className="dashboard-notice col-12" role="status">
      <AlertTriangle size={20} aria-hidden="true" />
      <div className="grow">
        <strong>{t.t('dashboard.incompleteTotals')}</strong>
        <p>
          {t.t('dashboard.incompleteTotalsHint', {
            count: missing.size,
            currency: data.household.baseCurrency,
          })}
        </p>
        <details>
          <summary>{t.t('dashboard.reviewMissing', { count: missing.size })}</summary>
          <ul className="dashboard-notice__entries">
            {transactions
              .filter((tx) => missing.has(tx.id))
              .map((tx) => (
                <li key={tx.id}>
                  <a href={hrefFor({ page: 'transactions', id: tx.id, date: tx.bookingDate })}>
                    <span>
                      {t.date(tx.bookingDate)} · {tx.merchantName || tx.description}
                    </span>
                    <Money minor={BigInt(tx.amountMinor)} currency={tx.currency} />
                  </a>
                </li>
              ))}
          </ul>
        </details>
      </div>
    </div>
  );
}
